import React, { useState, useEffect, useRef } from 'react';
import SearchableSelect from './SearchableSelect';
import weighingService from '@/services/weighing';
import { useAutocomplete } from '@/hooks/useAutocomplete';

interface DestinationSectionProps {
  formData: {
    destination: string;
    magasin: string;
    numeroLot: string;
  };
  onInputChange: (field: string, value: any) => void;
}

const DestinationSection: React.FC<DestinationSectionProps> = ({ formData, onInputChange }) => {
  const [magasins, setMagasins] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { values, loading: loadingAutocomplete } = useAutocomplete('destination');
  const datalistId = 'destination-list';
  const inputRef = useRef<HTMLInputElement>(null);

  // Charger les magasins au montage du composant
  useEffect(() => {
    loadMagasins();
  }, []);

  const loadMagasins = async () => {
    setLoading(true);
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await weighingService.getMagasins({ page_size: 1000 });
      if (response && response.results) {
        setMagasins(response.results || []);
      }
    } catch (error) {
      console.log('Magasins non disponibles:', error);
      setMagasins([]);
    } finally {
      setLoading(false);
    }
  };

  // Convertir les données en format pour SearchableSelect
  const magasinOptions = magasins.map(m => ({
    value: m.id.toString(),
    label: `${m.nom} - ${m.adresse || 'Adresse non renseignée'}`
  }));

  const handleDestinationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange('destination', e.target.value);
  };

  const handleDestinationBlur = () => {
    // La sauvegarde sera gérée après l'enregistrement de la pesée
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <h3 className="font-medium text-green-900 text-sm mb-3">DESTINATION / STOCKAGE</h3>
      
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-green-800 mb-1">Destination</label>
          <input
            ref={inputRef}
            type="text"
            list={datalistId}
            value={formData.destination}
            onChange={handleDestinationChange}
            onBlur={handleDestinationBlur}
            placeholder="Lieu de destination"
            className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm bg-white"
            disabled={loadingAutocomplete}
          />
          <datalist id={datalistId}>
            {values.map((value, index) => (
              <option key={index} value={value} />
            ))}
          </datalist>
          {loadingAutocomplete && (
            <div className="mt-1 text-xs text-gray-500">Chargement des suggestions...</div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-green-800 mb-1">Magasin</label>
          <SearchableSelect
            value={formData.magasin || ''}
            onChange={(value) => onInputChange('magasin', value)}
            options={magasinOptions}
            placeholder={loading ? "Chargement..." : "Sélectionner un magasin"}
            className="w-full"
            disabled={loading}
          />
          {loading && (
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500 mr-2"></div>
              Chargement des magasins...
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-green-800 mb-1">N° Lot</label>
          <input
            type="text"
            value={formData.numeroLot}
            onChange={(e) => onInputChange('numeroLot', e.target.value)}
            placeholder="Numéro de lot"
            className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm bg-white"
          />
        </div>
      </div>
    </div>
  );
};

export default DestinationSection;
